import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, FileCheck, Megaphone, DollarSign, X, CheckCheck } from "lucide-react";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllRead,
  NOTIFICATION_TYPES,
} from "../../services/PebbleNotificationService";

const TYPE_META = {
  [NOTIFICATION_TYPES.DOCUMENT_SIGNING_REQUIRED]: {
    icon: FileCheck,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  [NOTIFICATION_TYPES.DOCUMENT_SIGNED]: {
    icon: FileCheck,
    color: "text-green-600",
    bg: "bg-green-50",
  },
  [NOTIFICATION_TYPES.DOCUMENT_ACKNOWLEDGED]: {
    icon: FileCheck,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  [NOTIFICATION_TYPES.DOCUMENT_REMINDER]: {
    icon: FileCheck,
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
  [NOTIFICATION_TYPES.ANNOUNCEMENT]: {
    icon: Megaphone,
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
  [NOTIFICATION_TYPES.VENDOR_PAID]: {
    icon: DollarSign,
    color: "text-green-600",
    bg: "bg-green-50",
  },
};

const timeAgo = (ts) => {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * NotificationBell — drop-in bell icon with unread badge + dropdown.
 *
 * Props:
 *   userId        string  — the logged-in user's uid
 *   recipientType 'eo' | 'mps'
 */
const NotificationBell = ({ userId, recipientType }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadUnread = useCallback(async () => {
    if (!userId) return;
    const res = await getUnreadCount(userId, recipientType);
    if (res.success) setUnread(res.count);
  }, [userId, recipientType]);

  useEffect(() => {
    loadUnread();
    // Poll every 60 s so new notifications surface without a refresh
    const interval = setInterval(loadUnread, 60_000);
    return () => clearInterval(interval);
  }, [loadUnread]);

  const handleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    const res = await getNotifications(userId, recipientType, 20);
    if (res.success) setNotifications(res.notifications);
    setLoading(false);
    // Mark all read
    if (unread > 0) {
      await markAllRead(userId, recipientType);
      setUnread(0);
    }
  };

  const handleClick = async (n) => {
    if (!n.isRead) await markNotificationRead(n.id);
    setOpen(false);
    if (n.linkPath) navigate(n.linkPath);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-primary-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg transition">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* List */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const meta = TYPE_META[n.type] || {
                  icon: Bell,
                  color: "text-gray-500",
                  bg: "bg-gray-50",
                };
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition ${
                      !n.isRead ? "bg-primary-50/40" : ""
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 leading-snug">{n.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                    </div>
                    {!n.isRead && (
                      <span className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <button
                onClick={async () => {
                  await markAllRead(userId, recipientType);
                  setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
                  setUnread(0);
                }}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
